// src/utils/employeeUniqueness.js
// Tenant-scoped uniqueness enforcement for employee identity fields (email + punchId)
//
// Firestore has no unique constraints; we enforce uniqueness via lock documents and transactions:
//   tenants/{tenantId}/employeeEmails/{normalizedEmail}
//   tenants/{tenantId}/punchIds/{punchId}

import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';

import { db } from '../firebase';

export function normalizeEmployeeEmail(rawEmail) {
  return (rawEmail || '').toString().trim().toLowerCase();
}

export function normalizePunchId(rawPunchId) {
  // punchId is stored as 4 digits string, but keep generic trimming
  return (rawPunchId || '').toString().trim();
}

function emailLockRef(tenantId, normalizedEmail) {
  return doc(db, `tenants/${tenantId}/employeeEmails`, normalizedEmail);
}

function punchLockRef(tenantId, punchId) {
  return doc(db, `tenants/${tenantId}/punchIds`, punchId);
}

function employeeRef(tenantId, employeeId) {
  return doc(db, `tenants/${tenantId}/employees`, employeeId);
}

export function isUniqueConstraintError(err) {
  const code = err?.code || err?.message || '';
  return (
    code === 'employee-email-taken' ||
    code === 'employee-punchid-taken'
  );
}

export async function checkEmployeeEmailAvailable({ tenantId, email, excludeEmployeeId = null }) {
  const normalizedEmail = normalizeEmployeeEmail(email);
  if (!normalizedEmail) return { available: true, normalizedEmail: '' };

  // Availability checks are read-only and should not require transaction permissions.
  // If rules block this read, we treat it as "unknown" and let the save transaction enforce uniqueness.
  let lock = null;
  try {
    const lockSnap = await getDoc(emailLockRef(tenantId, normalizedEmail));
    lock = lockSnap.exists() ? lockSnap.data() : null;
  } catch (err) {
    if (err?.code === 'permission-denied') {
      return { available: true, normalizedEmail, unknown: true };
    }
    throw err;
  }

  if (!lock) return { available: true, normalizedEmail };
  if (excludeEmployeeId && lock.employeeId === excludeEmployeeId) return { available: true, normalizedEmail };
  return { available: false, normalizedEmail, employeeId: lock.employeeId };
}

// Update top-level employee fields with email uniqueness enforced.
// Pass only fields you want to change.
export async function updateEmployeeIdentityTransaction({
  tenantId,
  employeeId,
  updates,
}) {
  if (!tenantId) throw new Error('tenantId-required');
  if (!employeeId) throw new Error('employeeId-required');
  if (!updates || typeof updates !== 'object') throw new Error('updates-required');

  return await runTransaction(db, async (tx) => {
    const empRef = employeeRef(tenantId, employeeId);
    const empSnap = await tx.get(empRef);
    if (!empSnap.exists()) throw new Error('employee-not-found');

    const current = empSnap.data() || {};

    const nextEmail = Object.prototype.hasOwnProperty.call(updates, 'email')
      ? normalizeEmployeeEmail(updates.email)
      : normalizeEmployeeEmail(current.email);

    const nextEmployment = Object.prototype.hasOwnProperty.call(updates, 'employment')
      ? (updates.employment || {})
      : (current.employment || {});

    const currentEmployment = current.employment || {};

    const nextPunchId = normalizePunchId(
      Object.prototype.hasOwnProperty.call(nextEmployment, 'punchId')
        ? nextEmployment.punchId
        : currentEmployment.punchId
    );

    const prevEmail = normalizeEmployeeEmail(current.email);
    const prevPunchId = normalizePunchId(currentEmployment.punchId);

    // If email changes, claim new lock first
    if (nextEmail && nextEmail !== prevEmail) {
      const newEmailLock = emailLockRef(tenantId, nextEmail);
      const newEmailSnap = await tx.get(newEmailLock);
      if (newEmailSnap.exists() && newEmailSnap.data()?.employeeId !== employeeId) {
        throw new Error('employee-email-taken');
      }
      tx.set(newEmailLock, { employeeId, createdAt: serverTimestamp() }, { merge: true });
    }

    // If punchId changes, claim new lock first
    if (nextPunchId && nextPunchId !== prevPunchId) {
      const newPunchLock = punchLockRef(tenantId, nextPunchId);
      const newPunchSnap = await tx.get(newPunchLock);
      if (newPunchSnap.exists() && newPunchSnap.data()?.employeeId !== employeeId) {
        throw new Error('employee-punchid-taken');
      }
      tx.set(newPunchLock, { employeeId, createdAt: serverTimestamp() }, { merge: true });
    }

    // Apply employee update
    const nextDoc = { ...updates };
    if (Object.prototype.hasOwnProperty.call(nextDoc, 'email')) {
      nextDoc.email = nextEmail;
    }
    if (Object.prototype.hasOwnProperty.call(nextDoc, 'employment')) {
      // keep merged employment if only partial updates were passed
      nextDoc.employment = { ...currentEmployment, ...(updates.employment || {}) };
    }

    tx.update(empRef, nextDoc);

    // Clean up old locks after update
    if (prevEmail && nextEmail !== prevEmail) {
      tx.delete(emailLockRef(tenantId, prevEmail));
    }
    if (prevPunchId && nextPunchId !== prevPunchId) {
      tx.delete(punchLockRef(tenantId, prevPunchId));
    }

    return { ok: true };
  });
}
